import { isNil, isObject } from 'lodash';
import { mutateByFieldTypeSync } from './mutate/mutateSync';
import { reduceFullyQualifiedPath } from './path';
import { FormValueChild } from './types';

const PRE_CORRECTION_FIELDS = [
  '_transformed',
  '_entity',
  '_sanitized',
  '_value',
] as const;

// NOTE(wassim): reduceFullyQualifiedPath maps list items missing from `_idxs`
// (added, then removed on the order page) to this index.
const REMOVED_LIST_ITEM_INDEX = '-1';

type CorrectableField = Partial<
  Record<(typeof PRE_CORRECTION_FIELDS)[number] | '_corrected', unknown>
>;

export type FieldCorrection = {
  path: string;
  from: unknown;
  to: unknown;
};

const isSame = (a: unknown, b: unknown) =>
  JSON.stringify(a) === JSON.stringify(b);

const getPreCorrectionField = (field: CorrectableField) =>
  PRE_CORRECTION_FIELDS.find((key) => key in field);

const getPreCorrectionValue = (field: CorrectableField) => {
  const key = getPreCorrectionField(field);
  return isNil(key) ? undefined : field[key];
};

const isRemovedListItemPath = (reducedPath: string) =>
  reducedPath.split('.').includes(REMOVED_LIST_ITEM_INDEX);

export const hasBeenCorrected = (field: unknown): boolean => {
  if (!isObject(field)) return false;
  const f = field as CorrectableField;
  if (!('_corrected' in f) || isNil(f._corrected)) return false;
  const key = getPreCorrectionField(f);
  return isNil(key) || !isSame(f[key], f._corrected);
};

export const getFieldCorrections = (
  template: FormValueChild
): FieldCorrection[] => {
  const corrections: FieldCorrection[] = [];
  mutateByFieldTypeSync(template, {
    field: (child, path) => {
      if (isNil(path) || !hasBeenCorrected(child)) return child;
      const reducedPath = reduceFullyQualifiedPath(path, template);
      if (isNil(reducedPath) || isRemovedListItemPath(reducedPath)) {
        return child;
      }
      const field = child as CorrectableField;
      corrections.push({
        path: reducedPath,
        from: getPreCorrectionValue(field),
        to: field._corrected,
      });
      return child;
    },
  });
  return corrections;
};
